import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { generateAndSaveXLSX } from "../../../management-v2/import/useDownloadXLSX";
import { useGoogleMapsLoader } from "../../../map/layers/useGoogleMapsLoader";
import { useIncidentsLayer, type Incident } from "../../../map/layers/useIncidentsLayer";
import { useUserLocationsLayer, type UserLocation } from "../../../map/layers/useUserLocationsLayer";
import { useTripItemsLayer, type TripItem, type TripItemPopupState, type TripDetailUser, fetchTripDetail } from "../../../map/layers/useTripItemsLayer";
import { useFacilitiesLayer, type MapFacility, type FacilityPopupState } from "../../../map/layers/useFacilitiesLayer";
import { useLegacyFacilitiesLayer } from "../../../map/layers/useLegacyFacilitiesLayer";
import { useZoneShapesLayer, type MapZone, type ZoneKind, type ZoneClickData } from "../../../map/layers/useZoneShapesLayer";
import { useOverlaysLayer, type OverlayFeatureClickData } from "../../../map/layers/useOverlaysLayer";
import { useTrackingLayer, type TrackingPoint, type TrackingPopupState } from "../../../map/layers/useTrackingLayer";
import { useDevicesLayer, type MapDevice } from "../../../map/layers/useDevicesLayer";
import { MapControlsMenu } from "./MapControlsMenu";
import { TrackingMenu } from "./TrackingMenu";
import { IncidentDetailsModal } from "../../../../components/modals/IncidentDetailsModal";
import { TripDetailsModal } from "../../../../components/modals/TripDetailsModal";
import LoadingSpinner from "../../../../components/ui/LoadingSpinner";
import { useMapSettings } from "../../context/useMapSettings";
import { useAuth } from "../../../../context/user";
import { MassWarningModal } from "../../../../pages/WorldSearch/components/MassWarningModal";
import { useClientModules } from "../../../../context/clientModules";
import { usePermissions } from "../../../../context/modulePermissions";
import { DepartmentName } from "../../../../lib/departments/DepartmentName";
import "./index.css";

// --- Phone number formatter ---
// Formats E.164 or raw digit strings into a readable form.
// Examples: +14155552671 → +1 (415) 555-2671, +447911123456 → +44 7911 123456
// Falls back to the original string for unrecognised formats.
function formatPhone(raw: string): string {
    if (!raw) return raw;
    const digits = raw.replace(/\D/g, "");
    // US/CA: +1 NXX NXX XXXX
    if (raw.startsWith("+1") && digits.length === 11) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    // UK: +44 XXXX XXXXXX (mobile) or +44 XXXX XXX XXXX (landline — approximated)
    if (raw.startsWith("+44") && digits.length === 12) {
        return `+44 ${digits.slice(2, 6)} ${digits.slice(6)}`;
    }
    // Generic E.164: group last 7 digits as XXX XXXX, keep country code prefix
    if (raw.startsWith("+") && digits.length >= 7) {
        const cc = digits.slice(0, digits.length - 10 > 0 ? digits.length - 10 : 1);
        const rest = digits.slice(cc.length);
        const groups = rest.match(/.{1,4}/g) ?? [rest];
        return `+${cc} ${groups.join(" ")}`;
    }
    return raw;
}

// --- Constrained popup position ---
// Renders at the requested position then shifts if the element would clip a viewport edge.

const POPUP_MARGIN = 8; // px gap from viewport edge

function useConstrainedPosition(
    ref: React.RefObject<HTMLDivElement | null>,
    requested: { x: number; y: number },
    visible: boolean,
): { left: number; top: number } {
    const [pos, setPos] = useState({ left: requested.x + 12, top: requested.y - 8 });

    useLayoutEffect(() => {
        if (!visible) return;
        const el = ref.current;
        if (!el) return;
        const { offsetWidth: w, offsetHeight: h } = el;
        const vw = window.innerWidth;
        const vh = window.innerHeight;

        let left = requested.x + 12;
        let top  = requested.y - 8;

        if (left + w + POPUP_MARGIN > vw) left = requested.x - w - 12;
        if (left < POPUP_MARGIN) left = POPUP_MARGIN;
        if (top + h + POPUP_MARGIN > vh) top = vh - h - POPUP_MARGIN;
        if (top < POPUP_MARGIN) top = POPUP_MARGIN;

        setPos({ left, top });
    }, [requested.x, requested.y, visible]);

    return pos;
}

// --- Trip popup (fetches user details per unique tripID) ---


type TripPopupProps = {
    popup: TripItemPopupState;
    onClose: () => void;
    onViewTrip: (tripID: string, itemKey: string) => void;
    isSuperAdmin: boolean;
};

type TripDetailCache = { user: TripDetailUser; clientName: string };

function TripPopup({ popup, onClose, onViewTrip, isSuperAdmin }: TripPopupProps) {
    // Fetch trip details for each unique tripID
    const [details, setDetails] = useState<Record<string, TripDetailCache>>({});

    useEffect(() => {
        if (!popup.visible) return;
        const uniqueTripIDs = [...new Set(popup.items.map((i) => i.tripID))];
        for (const tripID of uniqueTripIDs) {
            if (details[tripID]) continue;
            fetchTripDetail(tripID)
                .then((d) => setDetails((prev) => ({ ...prev, [tripID]: { user: d.user, clientName: d.clientName } })))
                .catch(() => {});
        }
    }, [popup.visible, popup.items]); // eslint-disable-line

    const containerRef = useRef<HTMLDivElement>(null);
    const pos = useConstrainedPosition(containerRef, popup.position, popup.visible);

    if (!popup.visible || popup.items.length === 0) return null;

    return (
        <div
            ref={containerRef}
            className="fixed z-[100] w-[300px] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5 max-h-[400px] overflow-y-auto"
            style={{ left: pos.left, top: pos.top }}
        >
            <div className="flex items-center justify-between px-3 pb-2">
                <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                    {popup.items.length === 1 ? "1 trip item" : `${popup.items.length} trip items`}
                </span>
                <button
                    onClick={onClose}
                    className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                >×</button>
            </div>

            {popup.items.map((item, i) => {
                const detail = details[item.tripID];
                return (
                    <div key={`${item.tripID}-${i}`} className={`px-3 py-2 ${i === 0 ? "" : "border-t border-light-light-grey"}`}>
                        {/* User info */}
                        {detail ? (
                            <div className="mb-1.5">
                                <div className="text-[13px] font-semibold text-dark-dark-grey">
                                    {detail.user.firstName} {detail.user.lastName}
                                </div>
                                {detail.user.email && (
                                    <a href={`mailto:${detail.user.email}`} className="block text-[11px] text-blue no-underline hover:underline">{detail.user.email}</a>
                                )}
                                {detail.user.phoneNumber && (
                                    <a href={`tel:${detail.user.phoneNumber}`} className="block text-[11px] text-blue no-underline hover:underline">{formatPhone(detail.user.phoneNumber)}</a>
                                )}
                                {isSuperAdmin && detail.clientName && (
                                    <div className="text-[11px] text-grey">{detail.clientName}</div>
                                )}
                            </div>
                        ) : (
                            <div className="text-[11px] text-light-grey mb-1.5">Loading…</div>
                        )}

                        {/* Item info */}
                        <div className="text-[12px] font-medium text-dark-dark-grey mb-0.5 capitalize">
                            {item.subType.replace(/_/g, " ")}
                        </div>
                        <div className="text-[11px] text-grey mb-0.5">{item.formatted}</div>
                        <div className="text-[11px] text-light-grey mb-2">
                            {new Date(item.startTime).toLocaleDateString()} – {new Date(item.endTime).toLocaleDateString()}
                        </div>

                        <div className="flex gap-1.5 flex-wrap">
                            <button
                                onClick={() => {
                                    onViewTrip(item.tripID, `${item.type}:${item.subType}:${item.startTime}`);
                                }}
                                className="text-[11px] font-semibold text-blue bg-transparent border border-light-blue rounded-md px-2.5 py-1 cursor-pointer"
                            >
                                View Details →
                            </button>
                            <a
                                href={`/trips?id=${item.tripID}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[11px] font-semibold text-blue bg-transparent border border-light-blue rounded-md px-2.5 py-1 no-underline inline-block"
                            >
                                Show Trip ↗
                            </a>
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

const POPUP_PAGE_SIZE = 5;
const USER_POPUP_PAGE_SIZE = 4;

// --- Selector popup state ---
// Shown when a click hits multiple layer types simultaneously

type LayerHit = { type: "incidents"; data: Incident[] } | { type: "users"; data: UserLocation[] } | { type: "trips"; data: TripItem[] } | { type: "facilities"; data: MapFacility[] } | { type: "legacy-facilities"; data: MapFacility[] } | { type: "devices"; data: MapDevice[] };

type SelectorPopupState = {
    visible: boolean;
    position: { x: number; y: number };
    hits: LayerHit[];
};

// --- Tooltip state ---

type TooltipLine = { label: string; count: number };

type TooltipState = {
    visible: boolean;
    position: { x: number; y: number };
    lines: TooltipLine[];
};

function Map() {
    const googleLoaded = useGoogleMapsLoader(["drawing", "places", "marker"]);
    const mapDivRef = useRef<HTMLDivElement>(null);
    const mapRef = useRef<google.maps.Map | null>(null);
    const [map, setMap] = useState<google.maps.Map | null>(null);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const { isSuperAdmin, clientID: userClientID } = useAuth();
    const { settings, setMapType, setTrackingUserID } = useMapSettings();
    const { mapType, incidentFeeds, showIncidentRadius, clusterIncidents, autoReloadUsers, trackingUserID, trackingDeviceID, trackingDays, clientID: mapClientID } = settings;
    const { hasModule } = useClientModules();
    const { hasPermission, hasFullAccess } = usePermissions();
    const effectiveClientID = isSuperAdmin ? mapClientID : userClientID;

    // When tracking is active all other layers are hidden
    const effectiveMap = (trackingUserID || trackingDeviceID) ? null : map;

    const feedIDs = useMemo(() => incidentFeeds.map((f) => f.id), [incidentFeeds]);

    const {
        incidents,
        wsState,
        selectedIncident,
        modalOpen,
        setModalOpen,
        popupVisible,
        setPopupVisible,
        popupPosition,
        popupIncidents,
        popupPage,
        popupTotalCount,
        onPopupPageChange,
        onPopupSelectIncident,
        getHitsAtPoint: getIncidentHits,
        setHoveredIDs: setIncidentHoveredIDs,
        openPopupForIncidents,
    } = useIncidentsLayer(effectiveMap, feedIDs, showIncidentRadius, clusterIncidents);

    const [userSearch, setUserSearch] = useState("");

    const {
        popup: userPopup,
        setPopup: setUserPopup,
        getHitsAtPoint: getUserHits,
        setHoveredIDs: setUserHoveredIDs,
        openPopupForUsers,
        getUsersInPolygon,
        getUsersInCircle,
        userCount,
        usersLoading,
        userList,
    } = useUserLocationsLayer(effectiveMap, autoReloadUsers, userSearch);

    const {
        popup: tripPopup,
        setPopup: setTripPopup,
        getHitsAtPoint: getTripHits,
        setHoveredIDs: setTripHoveredIDs,
        openPopupForItems: openPopupForTrips,
        tripCount,
    } = useTripItemsLayer(effectiveMap);

    const {
        popup: facilityPopup,
        setPopup: setFacilityPopup,
        getHitsAtPoint: getFacilityHits,
        setHoveredIDs: setFacilityHoveredIDs,
        openPopupForFacilities,
        facilityCount,
    } = useFacilitiesLayer(effectiveMap);

    const {
        popup: legacyFacilityPopup,
        setPopup: setLegacyFacilityPopup,
        getHitsAtPoint: getLegacyFacilityHits,
        setHoveredIDs: setLegacyFacilityHoveredIDs,
        openPopupForFacilities: openPopupForLegacyFacilities,
        legacyFacilityCount,
    } = useLegacyFacilitiesLayer(effectiveMap);

    const {
        popup: devicePopup,
        setPopup: setDevicePopup,
        getHitsAtPoint: getDeviceHits,
        setHoveredIDs: setDeviceHoveredIDs,
        openPopupForDevices,
        deviceCount,
    } = useDevicesLayer(effectiveMap);

    const [zoneModalOpen, setZoneModalOpen] = useState(false);
    const [zoneModalData, setZoneModalData] = useState<ZoneClickData | null>(null);

    const { geofenceCount, safetyZoneCount } = useZoneShapesLayer(
        effectiveMap,
        (data) => { setZoneModalData(data); setZoneModalOpen(true); },
        (cursor) => {
            zoneHoveredRef.current = cursor === "pointer";
            forceCursorRef.current?.(cursor);
        },
    );

    const [overlayPopup, setOverlayPopup] = useState<OverlayFeatureClickData & { visible: boolean }>({
        visible: false, name: "", description: "", position: { x: 0, y: 0 },
    });
    const overlayPopupDivRef = useRef<HTMLDivElement>(null);
    const overlayPos = useConstrainedPosition(overlayPopupDivRef, overlayPopup.position, overlayPopup.visible);
    const setOverlayPopupRef = useRef(setOverlayPopup);
    setOverlayPopupRef.current = setOverlayPopup;

    const { fitOverlay } = useOverlaysLayer(
        effectiveMap,
        (data) => { overlayShapeClickedRef.current = true; setOverlayPopup({ ...data, visible: true }); },
        (cursor) => {
            zoneHoveredRef.current = cursor === "pointer";
            forceCursorRef.current?.(cursor);
        },
    );

    // --- Fullscreen ---
    const mapContainerRef = useRef<HTMLDivElement>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    useEffect(() => {
        const handler = () => {
            setIsFullscreen(!!document.fullscreenElement);
            if (mapRef.current) {
                google.maps.event.trigger(mapRef.current, "resize");
            }
        };
        document.addEventListener("fullscreenchange", handler);
        return () => document.removeEventListener("fullscreenchange", handler);
    }, []);
    function toggleFullscreen() {
        if (!document.fullscreenElement) {
            mapContainerRef.current?.requestFullscreen();
        } else {
            document.exitFullscreen();
        }
    }

    // --- Right-click coordinate popup ---
    const [rightClickPopup, setRightClickPopup] = useState<{ visible: boolean; lat: number; lng: number; x: number; y: number }>({ visible: false, lat: 0, lng: 0, x: 0, y: 0 });
    const setRightClickPopupRef = useRef(setRightClickPopup);
    setRightClickPopupRef.current = setRightClickPopup;
    const rightClickPopupDivRef = useRef<HTMLDivElement>(null);
    const rightClickPos = useConstrainedPosition(rightClickPopupDivRef, rightClickPopup, rightClickPopup.visible);

    // --- Tracking layer (always receives the real map) ---
    const [trackingPopup, setTrackingPopup] = useState<TrackingPopupState>({ visible: false, position: { x: 0, y: 0 }, points: [], latestEndTime: "", groupIndex: 0, groupCount: 0 });
    const trackingPopupRef = useRef<HTMLDivElement>(null);
    const trackingPos = useConstrainedPosition(trackingPopupRef, trackingPopup.position, trackingPopup.visible);

    // Convert a lat/lng to client-space coords relative to the map div
    function latLngToClientPos(lat: number, lng: number): { x: number; y: number } | null {
        if (!map) return null;
        const mapDiv = map.getDiv() as HTMLElement;
        const rect = mapDiv.getBoundingClientRect();
        const proj = map.getProjection();
        if (!proj) return null;
        const scale = Math.pow(2, map.getZoom() ?? 15);
        const center = proj.fromLatLngToPoint(map.getCenter()!);
        const pt = proj.fromLatLngToPoint(new google.maps.LatLng(lat, lng));
        if (!center || !pt) return null;
        return {
            x: rect.left + (pt.x - center.x) * scale + rect.width / 2,
            y: rect.top  + (pt.y - center.y) * scale + rect.height / 2,
        };
    }

    const { getHitsAtPoint: getTrackingHits, getGroupAt: getTrackingGroup, setHoveredIdx: setTrackingHoveredIdx } = useTrackingLayer(
        map, trackingUserID ?? null, trackingDays, trackingDeviceID ?? null
    );
    const getTrackingHitsRef = useRef(getTrackingHits);
    const getTrackingGroupRef = useRef(getTrackingGroup);
    const setTrackingHoveredIdxRef = useRef(setTrackingHoveredIdx);
    useEffect(() => {
        getTrackingHitsRef.current = getTrackingHits;
        getTrackingGroupRef.current = getTrackingGroup;
        setTrackingHoveredIdxRef.current = setTrackingHoveredIdx;
    });

    // --- Area select tool ---
    type AreaSelectMode = "polygon" | "circle" | null;
    const [areaSelectMode, setAreaSelectMode] = useState<AreaSelectMode>(null);
    const [areaSelectOpen, setAreaSelectOpen] = useState(false);
    const areaSelectManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
    const areaSelectShapeRef   = useRef<google.maps.Circle | google.maps.Polygon | null>(null);
    const areaSelectModeRef       = useRef<AreaSelectMode>(null);
    const areaSelectJustFinished  = useRef(false);
    const lastMousePosRef      = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
    const getUsersInPolygonRef = useRef(getUsersInPolygon);
    const getUsersInCircleRef  = useRef(getUsersInCircle);
    getUsersInPolygonRef.current = getUsersInPolygon;
    getUsersInCircleRef.current  = getUsersInCircle;

    // Create drawing manager when map is ready
    useEffect(() => {
        if (!map) return;
        const manager = new google.maps.drawing.DrawingManager({
            drawingMode: null,
            drawingControl: false,
            polygonOptions: {
                fillColor: "#2563eb", fillOpacity: 0.12,
                strokeColor: "#2563eb", strokeWeight: 2,
                editable: false, zIndex: 200,
            },
            circleOptions: {
                fillColor: "#2563eb", fillOpacity: 0.12,
                strokeColor: "#2563eb", strokeWeight: 2,
                editable: false, zIndex: 200,
            },
        });
        manager.setMap(map);
        areaSelectManagerRef.current = manager;

        const onPolygonComplete = (polygon: google.maps.Polygon) => {
            areaSelectShapeRef.current?.setMap(null);
            areaSelectShapeRef.current = polygon;
            manager.setDrawingMode(null);
            areaSelectModeRef.current = null;
            areaSelectJustFinished.current = true;
            setAreaSelectMode(null);
            const path = polygon.getPath().getArray();
            const matched = getUsersInPolygonRef.current(path);
            openPopupForUsersRef.current(matched, lastMousePosRef.current);
        };

        const onCircleComplete = (circle: google.maps.Circle) => {
            areaSelectShapeRef.current?.setMap(null);
            areaSelectShapeRef.current = circle;
            manager.setDrawingMode(null);
            areaSelectModeRef.current = null;
            // No areaSelectJustFinished flag here — circlecomplete fires on mouseup,
            // not after a dblclick, so there's no trailing DOM click to suppress.
            setAreaSelectMode(null);
            const matched = getUsersInCircleRef.current(circle.getCenter()!, circle.getRadius());
            openPopupForUsersRef.current(matched, lastMousePosRef.current);
        };

        google.maps.event.addListener(manager, "polygoncomplete", onPolygonComplete);
        google.maps.event.addListener(manager, "circlecomplete",  onCircleComplete);

        return () => {
            google.maps.event.clearInstanceListeners(manager);
            manager.setMap(null);
            areaSelectShapeRef.current?.setMap(null);
            areaSelectManagerRef.current = null;
        };
    }, [map]);

    // Sync mode → DrawingManager + cursor
    useEffect(() => {
        const mgr = areaSelectManagerRef.current;
        if (!mgr) return;
        areaSelectModeRef.current = areaSelectMode;
        if (areaSelectMode === "polygon") {
            mgr.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
            forceCursorRef.current?.("crosshair");
            zoneHoveredRef.current = true; // prevent processMouseMove from overriding cursor
        } else if (areaSelectMode === "circle") {
            mgr.setDrawingMode(google.maps.drawing.OverlayType.CIRCLE);
            forceCursorRef.current?.("crosshair");
            zoneHoveredRef.current = true;
        } else {
            mgr.setDrawingMode(null);
            forceCursorRef.current?.("grab");
            zoneHoveredRef.current = false;
        }
    }, [areaSelectMode]);

    function activateAreaSelect(mode: AreaSelectMode) {
        areaSelectShapeRef.current?.setMap(null);
        areaSelectShapeRef.current = null;
        setAreaSelectMode(mode);
        setAreaSelectOpen(false);
    }

    function clearAreaSelect() {
        areaSelectShapeRef.current?.setMap(null);
        areaSelectShapeRef.current = null;
        if (areaSelectManagerRef.current) {
            areaSelectManagerRef.current.setDrawingMode(null);
            setAreaSelectMode(null);
        }
    }

    // --- User popup pagination ---
    const [userPage, setUserPage] = useState(1);

    // --- Tracking point pagination ---
    const TRACKING_PAGE_SIZE = 4;
    const [trackingPage, setTrackingPage] = useState(1);

    // --- Facility pagination + modal ---
    const FACILITY_PAGE_SIZE = 4;
    const [facilityPage, setFacilityPage] = useState(1);
    const [facilityModalOpen, setFacilityModalOpen] = useState(false);
    const [facilityModalData, setFacilityModalData] = useState<{ id: string; name: string; address: string; clientName: string; guardianNames: string[] } | null>(null);
    const [facilityModalLoading, setFacilityModalLoading] = useState(false);

    // --- Trip details modal ---
    const [tripModalOpen, setTripModalOpen] = useState(false);
    const [tripModalID, setTripModalID] = useState<string | null>(null);
    const [tripModalItemKey, setTripModalItemKey] = useState<string>("");
    const [showSirenModal, setShowSirenModal] = useState(false);
    const [sirenUserIDs, setSirenUserIDs] = useState<string[]>([]);
    const showSirenButton = useMemo(
        () => hasModule("siren") && hasFullAccess("siren_alert") && (!isSuperAdmin || !!mapClientID),
        [hasModule, hasPermission, isSuperAdmin, mapClientID]
    );

    // --- Aggregated tooltip ---
    const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, position: { x: 0, y: 0 }, lines: [] });

    // --- Selector popup (shown when click hits multiple layer types) ---
    const [selectorPopup, setSelectorPopup] = useState<SelectorPopupState>({ visible: false, position: { x: 0, y: 0 }, hits: [] });

    // Lock map pan/zoom while drawing or any popup is open
    const anyPopupVisible = popupVisible || userPopup.visible || tripPopup.visible || facilityPopup.visible || legacyFacilityPopup.visible || devicePopup.visible || selectorPopup.visible || overlayPopup.visible;
    useEffect(() => {
        if (!map) return;
        const locked = !!areaSelectMode || anyPopupVisible;
        map.setOptions({
            gestureHandling: locked ? "none" : "greedy",
            zoomControl: !locked,
        });
    }, [map, areaSelectMode, anyPopupVisible]);

    // --- Popup refs for constrained positioning ---
    const selectorPopupRef  = useRef<HTMLDivElement>(null);
    const userPopupRef2     = useRef<HTMLDivElement>(null);
    const incidentPopupRef  = useRef<HTMLDivElement>(null);
    const facilityPopupRef       = useRef<HTMLDivElement>(null);
    const legacyFacilityPopupRef = useRef<HTMLDivElement>(null);
    const devicePopupRef         = useRef<HTMLDivElement>(null);

    const selectorPos       = useConstrainedPosition(selectorPopupRef,       selectorPopup.position,       selectorPopup.visible);
    const userPos           = useConstrainedPosition(userPopupRef2,           userPopup.position,           userPopup.visible);
    const incidentPos       = useConstrainedPosition(incidentPopupRef,        popupPosition,                popupVisible);
    const facilityPos       = useConstrainedPosition(facilityPopupRef,        facilityPopup.position,       facilityPopup.visible);
    const legacyFacilityPos = useConstrainedPosition(legacyFacilityPopupRef,  legacyFacilityPopup.position, legacyFacilityPopup.visible);
    const devicePos         = useConstrainedPosition(devicePopupRef,          devicePopup.position,         devicePopup.visible);

    // Refs to avoid stale closures in the mouse effect
    const userPopupRef = useRef(userPopup);
    useEffect(() => {
        userPopupRef.current = userPopup;
        if (userPopup.visible) setUserPage(1);
    }, [userPopup]);
    const tripPopupRef = useRef(tripPopup);
    useEffect(() => { tripPopupRef.current = tripPopup; }, [tripPopup]);
    const facilityPopupStateRef = useRef(facilityPopup);
    useEffect(() => {
        facilityPopupStateRef.current = facilityPopup;

        if (facilityPopup.visible) setFacilityPage(1);
    }, [facilityPopup]);

    const legacyFacilityPopupStateRef = useRef(legacyFacilityPopup);
    useEffect(() => { legacyFacilityPopupStateRef.current = legacyFacilityPopup; }, [legacyFacilityPopup]);

    const devicePopupStateRef = useRef(devicePopup);
    useEffect(() => { devicePopupStateRef.current = devicePopup; }, [devicePopup]);

    const getIncidentHitsRef = useRef(getIncidentHits);
    const setIncidentHoveredIDsRef = useRef(setIncidentHoveredIDs);
    const openPopupForIncidentsRef = useRef(openPopupForIncidents);
    const getUserHitsRef = useRef(getUserHits);
    const setUserHoveredIDsRef = useRef(setUserHoveredIDs);
    const openPopupForUsersRef = useRef(openPopupForUsers);
    const getTripHitsRef = useRef(getTripHits);
    const setTripHoveredIDsRef = useRef(setTripHoveredIDs);
    const openPopupForTripsRef = useRef(openPopupForTrips);
    const getFacilityHitsRef = useRef(getFacilityHits);
    const setFacilityHoveredIDsRef = useRef(setFacilityHoveredIDs);
    const openPopupForFacilitiesRef = useRef(openPopupForFacilities);
    const getLegacyFacilityHitsRef = useRef(getLegacyFacilityHits);
    const setLegacyFacilityHoveredIDsRef = useRef(setLegacyFacilityHoveredIDs);
    const openPopupForLegacyFacilitiesRef = useRef(openPopupForLegacyFacilities);
    const getDeviceHitsRef = useRef(getDeviceHits);
    const setDeviceHoveredIDsRef = useRef(setDeviceHoveredIDs);
    const openPopupForDevicesRef = useRef(openPopupForDevices);
    const tripModalIDRef = useRef(tripModalID);
    const setTripModalIDRef = useRef(setTripModalID);
    const setTripModalItemKeyRef = useRef(setTripModalItemKey);
    const setTripModalOpenRef = useRef(setTripModalOpen);
    const setTripPopupRef = useRef(setTripPopup);
    const tripPopupStateRef = useRef(tripPopup);

    const openTripOrModalRef = useRef((items: TripItem[], position: { x: number; y: number }) => {
        if (items.length === 1) {
            const item = items[0];
            setTripPopupRef.current({ ...tripPopupStateRef.current, visible: false });
            setTripModalIDRef.current(item.tripID);
            setTripModalItemKeyRef.current(`${item.type}:${item.subType}:${item.startTime}`);
            setTripModalOpenRef.current(true);
        } else {
            openPopupForTripsRef.current(items, position);
        }
    });

    useEffect(() => {
        getIncidentHitsRef.current = getIncidentHits;
        setIncidentHoveredIDsRef.current = setIncidentHoveredIDs;
        openPopupForIncidentsRef.current = openPopupForIncidents;
        getUserHitsRef.current = getUserHits;
        setUserHoveredIDsRef.current = setUserHoveredIDs;
        openPopupForUsersRef.current = openPopupForUsers;
        getTripHitsRef.current = getTripHits;
        setTripHoveredIDsRef.current = setTripHoveredIDs;
        openPopupForTripsRef.current = openPopupForTrips;
        getFacilityHitsRef.current = getFacilityHits;
        setFacilityHoveredIDsRef.current = setFacilityHoveredIDs;
        openPopupForFacilitiesRef.current = openPopupForFacilities;
        getLegacyFacilityHitsRef.current = getLegacyFacilityHits;
        setLegacyFacilityHoveredIDsRef.current = setLegacyFacilityHoveredIDs;
        openPopupForLegacyFacilitiesRef.current = openPopupForLegacyFacilities;
        getDeviceHitsRef.current = getDeviceHits;
        setDeviceHoveredIDsRef.current = setDeviceHoveredIDs;
        openPopupForDevicesRef.current = openPopupForDevices;
        tripModalIDRef.current = tripModalID;
        setTripModalIDRef.current = setTripModalID;
        setTripModalItemKeyRef.current = setTripModalItemKey;
        setTripModalOpenRef.current = setTripModalOpen;
        setTripPopupRef.current = setTripPopup;
        tripPopupStateRef.current = tripPopup;
        openTripOrModalRef.current = (items: TripItem[], position: { x: number; y: number }) => {
            if (items.length === 1) {
                const item = items[0];
                setTripPopupRef.current({ ...tripPopupStateRef.current, visible: false });
                setTripModalIDRef.current(item.tripID);
                setTripModalItemKeyRef.current(`${item.type}:${item.subType}:${item.startTime}`);
                setTripModalOpenRef.current(true);
            } else {
                openPopupForTripsRef.current(items, position);
            }
        };
    });

    // --- Shared forceCursor ref (populated by mouse handler effect) ---
    const forceCursorRef  = useRef<((cursor: string) => void) | undefined>(undefined);
    const zoneHoveredRef  = useRef(false);
    // Set to true by overlay shape clicks so the mapDiv click handler doesn't dismiss the popup
    const overlayShapeClickedRef = useRef(false);

    // --- Centralized mouse handling ---

    useEffect(() => {
        if (!map) return;

        const mapDiv = map.getDiv() as HTMLElement;
        if (!mapDiv.id) mapDiv.id = `map-div-${Math.random().toString(36).slice(2)}`;

        let isDragging = false;
        let mouseDownPos = { x: 0, y: 0 };
        let mouseMoveRafId: number | null = null;
        let pendingX = 0;
        let pendingY = 0;

        // Inject style rule for cursor management
        const styleEl = document.createElement("style");
        document.head.appendChild(styleEl);
        let styleRuleIndex = -1;

        const forceCursor = (cursor: string) => {
            if (styleRuleIndex >= 0) styleEl.sheet?.deleteRule(styleRuleIndex);
            styleRuleIndex = styleEl.sheet?.insertRule(
                `#${mapDiv.id} * { cursor: ${cursor} !important; }`, 0
            ) ?? -1;
            mapDiv.style.setProperty("cursor", cursor, "important");
        };

        forceCursor("grab");
        forceCursorRef.current = forceCursor;

        const processMouseMove = () => {
            mouseMoveRafId = null;
            if (areaSelectModeRef.current) return;
            const { incidents: incidentHitItems, hoveredID: incidentHoveredID } = getIncidentHitsRef.current(pendingX, pendingY);
            const { users: userHitUsers, hoveredID: userHoveredID } = getUserHitsRef.current(pendingX, pendingY);
            const { items: tripHitItems, hoveredID: tripHoveredID } = getTripHitsRef.current(pendingX, pendingY);
            const { facilities: facilityHitItems, hoveredID: facilityHoveredID } = getFacilityHitsRef.current(pendingX, pendingY);
            const { facilities: legacyFacilityHitItems, hoveredID: legacyFacilityHoveredID } = getLegacyFacilityHitsRef.current(pendingX, pendingY);
            const { devices: deviceHitItems, hoveredID: deviceHoveredID } = getDeviceHitsRef.current(pendingX, pendingY);
            const trackingHit = getTrackingHitsRef.current(pendingX, pendingY);

            setIncidentHoveredIDsRef.current(incidentHoveredID ? new Set([incidentHoveredID]) : new Set());
            setUserHoveredIDsRef.current(userHoveredID ? new Set([userHoveredID]) : new Set());
            setTripHoveredIDsRef.current(tripHoveredID ? new Set([tripHoveredID]) : new Set());
            setFacilityHoveredIDsRef.current(facilityHoveredID ? new Set([facilityHoveredID]) : new Set());
            setLegacyFacilityHoveredIDsRef.current(legacyFacilityHoveredID ? new Set([legacyFacilityHoveredID]) : new Set());
            setDeviceHoveredIDsRef.current(deviceHoveredID ? new Set([deviceHoveredID]) : new Set());
            setTrackingHoveredIdxRef.current(trackingHit ? trackingHit.nearestIdx : null);

            const hasAnyHit = incidentHitItems.length > 0 || userHitUsers.length > 0 || tripHitItems.length > 0 || facilityHitItems.length > 0 || legacyFacilityHitItems.length > 0 || deviceHitItems.length > 0 || !!trackingHit;
            if (!zoneHoveredRef.current) forceCursor(hasAnyHit ? "pointer" : "grab");

            if (hasAnyHit) {
                const lines: TooltipLine[] = [];
                if (incidentHitItems.length > 0) lines.push({ label: incidentHitItems.length === 1 ? "Incident" : "Incidents", count: incidentHitItems.length });
                if (userHitUsers.length > 0) lines.push({ label: userHitUsers.length === 1 ? "User" : "Users", count: userHitUsers.length });
                if (tripHitItems.length > 0) lines.push({ label: tripHitItems.length === 1 ? "Trip" : "Trips", count: tripHitItems.length });
                if (facilityHitItems.length > 0) lines.push({ label: facilityHitItems.length === 1 ? "Facility" : "Facilities", count: facilityHitItems.length });
                if (legacyFacilityHitItems.length > 0) lines.push({ label: legacyFacilityHitItems.length === 1 ? "Facility (Legacy)" : "Facilities (Legacy)", count: legacyFacilityHitItems.length });
                if (deviceHitItems.length > 0) lines.push({ label: deviceHitItems.length === 1 ? "Device" : "Devices", count: deviceHitItems.length });
                if (trackingHit) lines.push({ label: trackingHit.nearbyCount === 1 ? "Update" : "Updates", count: trackingHit.nearbyCount });
                setTooltip({ visible: true, position: { x: pendingX, y: pendingY }, lines });
            } else {
                setTooltip((t) => ({ ...t, visible: false }));
            }
        };

        const handleMouseMove = (e: MouseEvent) => {
            if (Math.hypot(e.clientX - mouseDownPos.x, e.clientY - mouseDownPos.y) > 5) isDragging = true;
            pendingX = e.clientX;
            pendingY = e.clientY;
            lastMousePosRef.current = { x: e.clientX, y: e.clientY };
            if (mouseMoveRafId === null) mouseMoveRafId = requestAnimationFrame(processMouseMove);
        };

        const handleMouseDown = (e: MouseEvent) => {
            mouseDownPos = { x: e.clientX, y: e.clientY };
            isDragging = false;
        };

        const handleClick = (e: MouseEvent) => {
            if (isDragging) { isDragging = false; return; }
            if (overlayShapeClickedRef.current) { overlayShapeClickedRef.current = false; return; }
            if (areaSelectModeRef.current) return;
            if (areaSelectJustFinished.current) { areaSelectJustFinished.current = false; return; }
            setRightClickPopupRef.current((p) => p.visible ? { ...p, visible: false } : p);

            const { incidents: incidentHitItems } = getIncidentHitsRef.current(e.clientX, e.clientY);
            const { users: userHitUsers } = getUserHitsRef.current(e.clientX, e.clientY);
            const { items: tripHitItems } = getTripHitsRef.current(e.clientX, e.clientY);
            const { facilities: facilityHitItems } = getFacilityHitsRef.current(e.clientX, e.clientY);
            const { facilities: legacyFacilityHitItems } = getLegacyFacilityHitsRef.current(e.clientX, e.clientY);
            const { devices: deviceHitItems } = getDeviceHitsRef.current(e.clientX, e.clientY);
            const trackingHit = getTrackingHitsRef.current(e.clientX, e.clientY);

            if (trackingHit) {
                const group = getTrackingGroupRef.current(trackingHit.groupIndex);
                if (group && map) {
                    map.panTo({ lat: group.lat, lng: group.lng });
                }
                // After pan settles, derive popup position from the point's screen coords
                requestAnimationFrame(() => {
                    const px = (group && latLngToClientPos(group.lat, group.lng)) ?? { x: e.clientX, y: e.clientY };
                    setTrackingPopup({ visible: true, position: px, points: trackingHit.points, latestEndTime: trackingHit.latestPoint.endTime, groupIndex: trackingHit.groupIndex, groupCount: trackingHit.groupCount });
                    setTrackingPage(1);
                });
                e.stopPropagation();
                return;
            }

            const hitTypes: LayerHit[] = [];
            if (incidentHitItems.length > 0) hitTypes.push({ type: "incidents", data: incidentHitItems });
            if (userHitUsers.length > 0) hitTypes.push({ type: "users", data: userHitUsers });
            if (tripHitItems.length > 0) hitTypes.push({ type: "trips", data: tripHitItems });
            if (facilityHitItems.length > 0) hitTypes.push({ type: "facilities", data: facilityHitItems });
            if (legacyFacilityHitItems.length > 0) hitTypes.push({ type: "legacy-facilities", data: legacyFacilityHitItems });
            if (deviceHitItems.length > 0) hitTypes.push({ type: "devices", data: deviceHitItems });

            if (hitTypes.length === 0) {
                setPopupVisible(false);
                setUserPopup({ ...userPopupRef.current, visible: false });
                setTripPopup({ ...tripPopupRef.current, visible: false });
                setFacilityPopup({ ...facilityPopupStateRef.current, visible: false });
                setLegacyFacilityPopup({ ...legacyFacilityPopupStateRef.current, visible: false });
                setDevicePopup({ ...devicePopupStateRef.current, visible: false });
                setSelectorPopup((s) => ({ ...s, visible: false }));
                setOverlayPopupRef.current((p) => ({ ...p, visible: false }));
                setTrackingPopup((p) => ({ ...p, visible: false }));
                setAreaSelectOpen(false);
                areaSelectShapeRef.current?.setMap(null);
                areaSelectShapeRef.current = null;
                return;
            }

            e.stopPropagation();
            setTooltip((t) => ({ ...t, visible: false }));

            if (hitTypes.length === 1) {
                setSelectorPopup((s) => ({ ...s, visible: false }));
                if (hitTypes[0].type === "incidents") {
                    openPopupForIncidentsRef.current(hitTypes[0].data, { x: e.clientX, y: e.clientY });
                } else if (hitTypes[0].type === "users") {
                    openPopupForUsersRef.current(hitTypes[0].data as UserLocation[], { x: e.clientX, y: e.clientY });
                } else if (hitTypes[0].type === "trips") {
                    openTripOrModalRef.current(hitTypes[0].data as TripItem[], { x: e.clientX, y: e.clientY });
                } else if (hitTypes[0].type === "legacy-facilities") {
                    openPopupForLegacyFacilitiesRef.current(hitTypes[0].data as MapFacility[], { x: e.clientX, y: e.clientY });
                } else if (hitTypes[0].type === "devices") {
                    openPopupForDevicesRef.current(hitTypes[0].data as MapDevice[], { x: e.clientX, y: e.clientY });
                } else {
                    openPopupForFacilitiesRef.current(hitTypes[0].data as MapFacility[], { x: e.clientX, y: e.clientY });
                }
            } else {
                setPopupVisible(false);
                setUserPopup({ ...userPopupRef.current, visible: false });
                setTripPopup({ ...tripPopupRef.current, visible: false });
                setFacilityPopup({ ...facilityPopupStateRef.current, visible: false });
                setLegacyFacilityPopup({ ...legacyFacilityPopupStateRef.current, visible: false });
                setDevicePopup({ ...devicePopupStateRef.current, visible: false });
                setSelectorPopup({ visible: true, position: { x: e.clientX, y: e.clientY }, hits: hitTypes });
            }
        };

        const handleContextMenu = (e: MouseEvent) => {
            e.preventDefault();
            const proj = map.getProjection();
            if (!proj) return;
            const rect = mapDiv.getBoundingClientRect();
            const scale = Math.pow(2, map.getZoom() ?? 3);
            const center = proj.fromLatLngToPoint(map.getCenter()!);
            if (!center) return;
            const worldX = center.x + (e.clientX - rect.left - rect.width / 2) / scale;
            const worldY = center.y + (e.clientY - rect.top - rect.height / 2) / scale;
            const latLng = proj.fromPointToLatLng(new google.maps.Point(worldX, worldY));
            if (!latLng) return;
            setRightClickPopupRef.current({ visible: true, lat: latLng.lat(), lng: latLng.lng(), x: e.clientX, y: e.clientY });
        };

        mapDiv.addEventListener("mousemove", handleMouseMove);
        mapDiv.addEventListener("mousedown", handleMouseDown);
        mapDiv.addEventListener("click", handleClick);
        mapDiv.addEventListener("contextmenu", handleContextMenu);

        return () => {
            mapDiv.removeEventListener("mousemove", handleMouseMove);
            mapDiv.removeEventListener("mousedown", handleMouseDown);
            mapDiv.removeEventListener("click", handleClick);
            mapDiv.removeEventListener("contextmenu", handleContextMenu);
            styleEl.remove();
            if (mouseMoveRafId !== null) cancelAnimationFrame(mouseMoveRafId);
        };
    }, [map]); // run once per map instance

    // Init map
    useEffect(() => {
        if (!googleLoaded || !mapDivRef.current) return;

        const map = new google.maps.Map(mapDivRef.current, {
            center: { lat: 20, lng: 0 },
            zoom: 3,
            minZoom: 2,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            gestureHandling: "greedy",
            mapId: process.env.REACT_APP_GOOGLE_MAP_ID,
            backgroundColor: "#6cd2e7",
            isFractionalZoomEnabled: false,
            restriction: {
                latLngBounds: { north: 85, south: -85, west: -180, east: 180 },
                strictBounds: true,
            },
        });

        mapRef.current = map;
        setMap(map);

        if (searchInputRef.current && window.google?.maps?.places) {
            const autocomplete = new window.google.maps.places.Autocomplete(searchInputRef.current, {
                fields: ["geometry", "name", "formatted_address"],
            });
            autocomplete.addListener("place_changed", () => {
                const place = autocomplete.getPlace();
                if (place.geometry?.location) {
                    map.setCenter(place.geometry.location);
                    map.setZoom(12);
                }
            });
        }
    }, [googleLoaded]);

    // Sync map type
    useEffect(() => {
        if (!mapRef.current) return;
        mapRef.current.setMapTypeId(mapType);
    }, [mapType]);

    if (!googleLoaded) {
        return (
            <div className="h-full w-full flex justify-center items-center">
                <LoadingSpinner />
            </div>
        );
    }

    const totalPages = Math.ceil(popupTotalCount / POPUP_PAGE_SIZE);

    return (
        <div ref={mapContainerRef} className="relative h-full w-full">
            <MapControlsMenu
                wsState={wsState}
                incidentCount={incidents.length}
                userCount={userCount}
                usersLoading={usersLoading}
                tripCount={tripCount}
                facilityCount={facilityCount}
                legacyFacilityCount={legacyFacilityCount}
                geofenceCount={geofenceCount}
                safetyZoneCount={safetyZoneCount}
                deviceCount={deviceCount}
                onFitOverlay={fitOverlay}
                areaSelectMode={areaSelectMode}
                areaSelectOpen={areaSelectOpen}
                onAreaSelectToggle={() => setAreaSelectOpen((o) => !o)}
                onAreaSelectMode={activateAreaSelect}
                onUserSearchChange={setUserSearch}
                userList={userList}
            />


            {/* Location Search */}
            <div className="absolute top-4 right-4 z-40 w-80">
                <input
                    ref={searchInputRef}
                    type="text"
                    placeholder="Search for a location..."
                    className="w-full px-4 py-2 rounded-md shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
                />
            </div>

            {/* Display Mode Switch */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-50">
                <div className="bg-white rounded-lg shadow-md flex overflow-hidden">
                    {(["roadmap", "terrain", "hybrid", "satellite"] as const).map((type, i) => (
                        <button
                            key={type}
                            onClick={() => setMapType(type)}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${i > 0 ? "border-l border-light-light-grey" : ""} ${
                                mapType === type
                                    ? "bg-blue text-white"
                                    : "bg-white text-grey hover:bg-light-light-grey"
                            }`}
                        >
                            {type.charAt(0).toUpperCase() + type.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Fullscreen button */}
            <button
                onClick={toggleFullscreen}
                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                className="absolute bottom-4 left-4 z-50 flex items-center justify-center w-10 h-10 rounded-lg bg-white shadow-[0_2px_8px_rgba(0,0,0,0.18)] text-grey hover:bg-ice border-none cursor-pointer"
            >
                <span className="material-icons text-[20px] leading-none">
                    {isFullscreen ? "fullscreen_exit" : "fullscreen"}
                </span>
            </button>

            {/* Right-click coordinate popup */}
            {rightClickPopup.visible && (
                <div
                    ref={rightClickPopupDivRef}
                    className="fixed z-[60] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5 px-3 min-w-[180px]"
                    style={{ left: rightClickPos.left, top: rightClickPos.top }}
                >
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold text-grey uppercase tracking-[0.05em]">Coordinates</span>
                        <button
                            onClick={() => setRightClickPopup((p) => ({ ...p, visible: false }))}
                            className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                        >×</button>
                    </div>
                    <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-1 text-[12px] mb-2">
                        <span className="text-light-grey">Latitude</span>
                        <span className="text-dark-dark-grey">{rightClickPopup.lat.toFixed(6)}</span>
                        <span className="text-light-grey">Longitude</span>
                        <span className="text-dark-dark-grey">{rightClickPopup.lng.toFixed(6)}</span>
                    </div>
                    <button
                        onClick={() => {
                            navigator.clipboard.writeText(`${rightClickPopup.lat.toFixed(6)}, ${rightClickPopup.lng.toFixed(6)}`);
                            setRightClickPopup((p) => ({ ...p, visible: false }));
                        }}
                        className="w-full text-[12px] font-medium text-blue bg-transparent border border-light-light-grey rounded-md py-1 cursor-pointer hover:bg-ice"
                    >
                        Copy
                    </button>
                </div>
            )}

            {/* Aggregated hover tooltip */}
            {tooltip.visible && tooltip.lines.length > 0 && (
                <div
                    className="fixed z-[100] bg-black/75 text-white text-[12px] px-2 py-1 rounded pointer-events-none whitespace-nowrap leading-relaxed"
                    style={{
                        left: tooltip.position.x + 14,
                        top: tooltip.position.y - (tooltip.lines.length > 1 ? 44 : 28),
                    }}
                >
                    {tooltip.lines.map((line) => (
                        <div key={line.label}>{line.count} {line.label}</div>
                    ))}
                </div>
            )}

            {/* Layer selector popup — shown when click hits multiple layer types */}
            {selectorPopup.visible && (
                <div
                    ref={selectorPopupRef}
                    className="fixed z-[101] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5 min-w-[180px]"
                    style={{ left: selectorPos.left, top: selectorPos.top }}
                >
                    <div className="flex items-center justify-between px-3 pb-2">
                        <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                            View
                        </span>
                        <button
                            onClick={() => setSelectorPopup((s) => ({ ...s, visible: false }))}
                            className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                        >×</button>
                    </div>
                    {selectorPopup.hits.map((hit) => {
                        const label = hit.type === "incidents" ? "Incidents"
                            : hit.type === "users" ? "Users"
                            : hit.type === "trips" ? "Trips"
                            : hit.type === "devices" ? "Devices"
                            : hit.type === "legacy-facilities" ? "Facilities (Legacy)"
                            : "Facilities";
                        return (
                            <button
                                key={hit.type}
                                onClick={() => {
                                    setSelectorPopup((s) => ({ ...s, visible: false }));
                                    if (hit.type === "incidents") {
                                        openPopupForIncidentsRef.current(hit.data, selectorPopup.position);
                                    } else if (hit.type === "users") {
                                        openPopupForUsersRef.current(hit.data as UserLocation[], selectorPopup.position);
                                    } else if (hit.type === "trips") {
                                        openTripOrModalRef.current(hit.data as TripItem[], selectorPopup.position);
                                    } else if (hit.type === "legacy-facilities") {
                                        openPopupForLegacyFacilitiesRef.current(hit.data as MapFacility[], selectorPopup.position);
                                    } else if (hit.type === "devices") {
                                        openPopupForDevicesRef.current(hit.data as MapDevice[], selectorPopup.position);
                                    } else {
                                        openPopupForFacilitiesRef.current(hit.data as MapFacility[], selectorPopup.position);
                                    }
                                }}
                                className="grid grid-cols-[1fr_auto] items-center w-full px-3 py-2 bg-transparent border-none border-t border-light-light-grey cursor-pointer hover:bg-ice"
                            >
                                <span className="text-[13px] text-dark-dark-grey text-left">{label}</span>
                                <span className="text-[13px] font-semibold text-dark-dark-grey tabular-nums">{hit.data.length}</span>
                            </button>
                        );
                    })}
                </div>
            )}

            {/* User location popup */}
            {userPopup.visible && userPopup.users.length > 0 && (() => {
                const userTotalPages = Math.ceil(userPopup.users.length / USER_POPUP_PAGE_SIZE);
                const pageUsers = userPopup.users.slice((userPage - 1) * USER_POPUP_PAGE_SIZE, userPage * USER_POPUP_PAGE_SIZE);
                return (
                    <div
                        ref={userPopupRef2}
                        className="fixed z-[100] w-[300px] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5"
                        style={{ left: userPos.left, top: userPos.top }}
                    >
                        <div className="flex items-center justify-between px-3 pb-2">
                            <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                                {userPopup.users.length === 1 ? "1 user" : `${userPopup.users.length} users`}
                            </span>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => {
                                        const headers = [
                                            "First Name", "Middle Names", "Last Name",
                                            ...(isSuperAdmin ? ["Client"] : []),
                                            "Department", "Latitude", "Longitude",
                                            "Time", "Mobile Phone", "Personal Mobile", "Home Phone", "Phone Numbers", "Email",
                                        ];
                                        const rows = userPopup.users.map((u) => {
                                            const get = (label: string) => u.phones.find(p => p.label === label)?.number ?? "";
                                            const others = u.phones.filter(p => p.label === "Phone").map(p => p.number).join(", ");
                                            return [
                                                u.firstName, u.middleNames, u.lastName,
                                                ...(isSuperAdmin ? [u.clientName] : []),
                                                u.departmentName,
                                                u.lat.toFixed(5), u.lng.toFixed(5),
                                                u.receivedAt, get("Mobile"), get("Personal Mobile"), get("Home"), others, u.email,
                                            ];
                                        });
                                        generateAndSaveXLSX("users.xlsx", headers, rows);
                                    }}
                                    title="Download as XLSX"
                                    className="bg-transparent border-none cursor-pointer text-light-grey text-[11px] leading-none p-0 hover:text-blue"
                                >
                                    ↓ Download
                                </button>
                                {showSirenButton && (
                                    <button
                                        onClick={() => {
                                            setSirenUserIDs(userPopup.users.map(u => u.id));
                                            setUserPopup({ ...userPopup, visible: false });
                                            setShowSirenModal(true);
                                        }}
                                        title="Send Siren Alert"
                                        className="flex items-center gap-0.5 bg-transparent border-none cursor-pointer text-light-grey text-[11px] leading-none p-0 hover:text-blue"
                                    >
                                        <span className="material-icons" style={{fontSize: 13}}>notifications_active</span>
                                        Siren Alert
                                    </button>
                                )}
                                <button
                                    onClick={() => { setUserPopup({ ...userPopup, visible: false }); clearAreaSelect(); }}
                                    className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                                >×</button>
                            </div>
                        </div>
                        {pageUsers.map((user) => (
                            <div key={user.id} className="px-3 py-2 border-t border-light-light-grey">
                                <div className="text-[13px] font-semibold text-dark-dark-grey mb-1">{user.name}</div>
                                <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] mb-1">
                                    <span className="text-light-grey">Source</span>
                                    <span className="text-grey">{user.source.charAt(0).toUpperCase() + user.source.slice(1)}</span>
                                    {user.departmentID && <>
                                        <span className="text-light-grey">Department</span>
                                        <span className="text-grey"><DepartmentName departmentID={user.departmentID} /></span>
                                    </>}
                                    {isSuperAdmin && user.clientName && <>
                                        <span className="text-light-grey">Client</span>
                                        <span className="text-grey">{user.clientName}</span>
                                    </>}
                                </div>
                                {user.email && (
                                    <a href={`mailto:${user.email}`} className="block text-[11px] text-blue mb-0.5 no-underline hover:underline">{user.email}</a>
                                )}
                                {user.phones.length > 0 ? (
                                    <div className="flex flex-col mb-1">
                                        {user.phones.map((p) => (
                                            <a key={p.label} href={`tel:${p.number}`} className="text-[11px] text-blue no-underline hover:underline">
                                                {p.label}: {formatPhone(p.number)}
                                            </a>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="text-[11px] text-light-grey mb-1">No phone number</div>
                                )}
                                <div className="flex items-center gap-2 flex-wrap">
                                    <a
                                        href={user.worldSearchUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-[11px] font-semibold text-blue bg-transparent border border-light-blue rounded-md px-2.5 py-1 no-underline inline-block"
                                    >
                                        World Search ↗
                                    </a>
                                    <button
                                        onClick={() => {
                                            setTrackingUserID(user.id);
                                            setUserPopup({ ...userPopup, visible: false });
                                        }}
                                        className={`text-[11px] font-semibold rounded-md px-2.5 py-1 border cursor-pointer ${
                                            trackingUserID === user.id
                                                ? "bg-global-purple text-white border-global-purple"
                                                : "bg-transparent text-global-purple border-global-purple hover:bg-global-purple hover:text-white"
                                        }`}
                                    >
                                        {trackingUserID === user.id ? "Tracking" : "Track"}
                                    </button>
                                </div>
                            </div>
                        ))}
                        {userTotalPages > 1 && (
                            <div className="flex items-center justify-between px-3 pt-2 border-t border-light-light-grey">
                                <button
                                    disabled={userPage === 1}
                                    onClick={() => setUserPage((p) => p - 1)}
                                    className={`text-[12px] bg-transparent border-none p-0 ${userPage === 1 ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >
                                    ← Prev
                                </button>
                                <span className="text-[11px] text-light-grey">{userPage} / {userTotalPages} · {userPopup.users.length} users</span>
                                <button
                                    disabled={userPage === userTotalPages}
                                    onClick={() => setUserPage((p) => p + 1)}
                                    className={`text-[12px] bg-transparent border-none p-0 ${userPage === userTotalPages ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >
                                    Next →
                                </button>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Device popup */}
            {devicePopup.visible && devicePopup.devices.length > 0 && (
                <div
                    ref={devicePopupRef}
                    className="fixed z-[100] w-[280px] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5 max-h-[400px] overflow-y-auto"
                    style={{ left: devicePos.left, top: devicePos.top }}
                >
                    <div className="flex items-center justify-between px-3 pb-2">
                        <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                            {devicePopup.devices.length === 1 ? "1 device" : `${devicePopup.devices.length} devices`}
                        </span>
                        <button
                            onClick={() => setDevicePopup({ ...devicePopup, visible: false })}
                            className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                        >×</button>
                    </div>
                    {devicePopup.devices.map((device, i) => (
                        <div key={device._id} className={`px-3 py-2 ${i === 0 ? "" : "border-t border-light-light-grey"}`}>
                            <div className="text-[13px] font-semibold text-dark-dark-grey mb-1">{device.name || device.deviceID}</div>
                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                                {device.type && <>
                                    <span className="text-light-grey">Type</span>
                                    <span className="text-grey capitalize">{device.type}{device.subtype ? ` · ${device.subtype}` : ""}</span>
                                </>}
                                {device.departmentID && <>
                                    <span className="text-light-grey">Department</span>
                                    <span className="text-grey"><DepartmentName departmentID={device.departmentID} /></span>
                                </>}
                                {isSuperAdmin && device.clientName && <>
                                    <span className="text-light-grey">Client</span>
                                    <span className="text-grey">{device.clientName}</span>
                                </>}
                                <span className="text-light-grey">Updated</span>
                                <span className="text-grey">{new Date(device.receivedAt).toLocaleString()}</span>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* Tracking point popup */}
            {trackingPopup.visible && trackingPopup.points.length > 0 && (
                <div
                    ref={trackingPopupRef}
                    className="fixed z-[60] w-[260px] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5"
                    style={{ left: trackingPos.left, top: trackingPos.top }}
                >
                    <div className="flex items-center justify-between px-3 pb-2">
                        <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                            Location update
                        </span>
                        <button
                            onClick={() => setTrackingPopup((p) => ({ ...p, visible: false }))}
                            className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                        >×</button>
                    </div>
                    {trackingPopup.groupCount > 1 && (
                        <div className="flex items-center justify-between px-3 pb-2 border-b border-light-light-grey">
                            <button
                                disabled={trackingPopup.groupIndex === 0}
                                onClick={() => {
                                    const g = getTrackingGroupRef.current(trackingPopup.groupIndex - 1);
                                    if (!g || !map) return;
                                    map.panTo({ lat: g.lat, lng: g.lng });
                                    requestAnimationFrame(() => {
                                        const px = latLngToClientPos(g.lat, g.lng);
                                        setTrackingPopup((p) => ({ ...p, points: g.points, groupIndex: p.groupIndex - 1, ...(px ? { position: px } : {}) }));
                                        setTrackingPage(1);
                                    });
                                }}
                                className="flex items-center gap-1 text-[12px] text-global-purple bg-transparent border-none cursor-pointer p-0 disabled:opacity-30 disabled:cursor-default"
                            >
                                <span className="material-icons text-[16px] leading-none">chevron_left</span>
                                Prev
                            </button>
                            <span className="text-[11px] text-light-grey">{trackingPopup.groupIndex + 1} / {trackingPopup.groupCount}</span>
                            <button
                                disabled={trackingPopup.groupIndex === trackingPopup.groupCount - 1}
                                onClick={() => {
                                    const g = getTrackingGroupRef.current(trackingPopup.groupIndex + 1);
                                    if (!g || !map) return;
                                    map.panTo({ lat: g.lat, lng: g.lng });
                                    requestAnimationFrame(() => {
                                        const px = latLngToClientPos(g.lat, g.lng);
                                        setTrackingPopup((p) => ({ ...p, points: g.points, groupIndex: p.groupIndex + 1, ...(px ? { position: px } : {}) }));
                                        setTrackingPage(1);
                                    });
                                }}
                                className="flex items-center gap-1 text-[12px] text-global-purple bg-transparent border-none cursor-pointer p-0 disabled:opacity-30 disabled:cursor-default"
                            >
                                Next
                                <span className="material-icons text-[16px] leading-none">chevron_right</span>
                            </button>
                        </div>
                    )}
                    {(() => {
                        const totalPoints = trackingPopup.points.length;
                        const totalPages = Math.ceil(totalPoints / TRACKING_PAGE_SIZE);
                        const pagePoints = totalPoints > TRACKING_PAGE_SIZE
                            ? trackingPopup.points.slice((trackingPage - 1) * TRACKING_PAGE_SIZE, trackingPage * TRACKING_PAGE_SIZE)
                            : trackingPopup.points;
                        return (
                            <>
                                {pagePoints.map((pt, i) => {
                                    const isLatest = pt.endTime === trackingPopup.latestEndTime;
                                    const battery = pt.battery && pt.battery.length > 0
                                        ? (pt.pointCount === 1 ? `${pt.battery[0]}%` : `${pt.battery[pt.battery.length - 1]}% → ${pt.battery[0]}%`)
                                        : null;
                                    const speed = pt.speed && pt.speed.length > 0 ? `${pt.speed[pt.speed.length - 1].toFixed(1)} m/s` : null;
                                    const time = new Date(pt.endTime).toLocaleString();
                                    return (
                                        <div key={i} className="px-3 py-2 border-t border-light-light-grey">
                                            {isLatest && (
                                                <div className="flex items-center gap-1.5 mb-1">
                                                    <span className="w-1.5 h-1.5 rounded-full bg-global-purple animate-pulse flex-shrink-0" />
                                                    <span className="text-[11px] font-semibold text-global-purple uppercase tracking-[0.05em]">Latest</span>
                                                </div>
                                            )}
                                            <div className="text-[12px] text-dark-dark-grey mb-1">{time}</div>
                                            <div className="grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px]">
                                                <span className="text-light-grey">Battery</span>
                                                {battery ? <span className="text-dark-dark-grey">{battery}</span> : <span className="text-light-grey">N/A</span>}
                                                <span className="text-light-grey">Speed</span>
                                                {speed ? <span className="text-dark-dark-grey">{speed}</span> : <span className="text-light-grey">N/A</span>}
                                                <span className="text-light-grey">Bearing</span>
                                                {pt.bearing != null ? <span className="text-dark-dark-grey">{Math.round(pt.bearing)}°</span> : <span className="text-light-grey">N/A</span>}
                                            </div>
                                        </div>
                                    );
                                })}
                                {totalPages > 1 && (
                                    <div className="flex items-center justify-between px-3 pt-2 border-t border-light-light-grey">
                                        <button
                                            disabled={trackingPage === 1}
                                            onClick={() => setTrackingPage((p) => p - 1)}
                                            className={`text-[12px] bg-transparent border-none p-0 ${trackingPage === 1 ? "text-light-light-grey cursor-default" : "text-global-purple cursor-pointer"}`}
                                        >← Prev</button>
                                        <span className="text-[11px] text-light-grey">{trackingPage} / {totalPages}</span>
                                        <button
                                            disabled={trackingPage === totalPages}
                                            onClick={() => setTrackingPage((p) => p + 1)}
                                            className={`text-[12px] bg-transparent border-none p-0 ${trackingPage === totalPages ? "text-light-light-grey cursor-default" : "text-global-purple cursor-pointer"}`}
                                        >Next →</button>
                                    </div>
                                )}
                            </>
                        );
                    })()}
                </div>
            )}

            {/* Trip items popup */}
            <TripPopup
                popup={tripPopup}
                isSuperAdmin={isSuperAdmin}
                onClose={() => setTripPopup({ ...tripPopup, visible: false })}
                onViewTrip={(tripID, itemKey) => {
                    setTripModalID(tripID);
                    setTripModalItemKey(itemKey);
                    setTripModalOpen(true);
                    setTripPopup({ ...tripPopup, visible: false });
                }}
            />

            {/* Facility popup */}
            {facilityPopup.visible && facilityPopup.facilities.length > 0 && (() => {
                const facilityTotalPages = Math.ceil(facilityPopup.facilities.length / FACILITY_PAGE_SIZE);
                const pageFacilities = facilityPopup.facilities.slice((facilityPage - 1) * FACILITY_PAGE_SIZE, facilityPage * FACILITY_PAGE_SIZE);
                return (
                    <div
                        ref={facilityPopupRef}
                        className="fixed z-[100] w-[280px] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5"
                        style={{ left: facilityPos.left, top: facilityPos.top }}
                    >
                        <div className="flex items-center justify-between px-3 pb-2">
                            <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                                {facilityPopup.facilities.length === 1 ? "1 facility" : `${facilityPopup.facilities.length} facilities`}
                            </span>
                            <button
                                onClick={() => setFacilityPopup({ ...facilityPopup, visible: false })}
                                className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                            >×</button>
                        </div>
                        {pageFacilities.map((f) => (
                            <div key={f.id} className="px-3 py-2 border-t border-light-light-grey">
                                <div className="text-[13px] font-semibold text-dark-dark-grey mb-0.5">{f.name}</div>
                                {f.address && <div className="text-[11px] text-light-grey mb-1">{f.address}</div>}
                                {isSuperAdmin && f.clientName && (
                                    <div className="text-[11px] text-grey mb-1">{f.clientName}</div>
                                )}
                                <button
                                    onClick={() => {
                                        setFacilityModalData({ id: f.id, name: f.name, address: f.address, clientName: f.clientName, guardianNames: f.guardianNames ?? [] });
                                        setFacilityModalOpen(true);
                                    }}
                                    className="text-[11px] font-semibold text-blue bg-transparent border border-light-blue rounded-md px-2.5 py-1 cursor-pointer inline-block"
                                >
                                    View Details →
                                </button>
                            </div>
                        ))}
                        {facilityTotalPages > 1 && (
                            <div className="flex items-center justify-between px-3 pt-2 border-t border-light-light-grey">
                                <button
                                    disabled={facilityPage === 1}
                                    onClick={() => setFacilityPage((p) => p - 1)}
                                    className={`text-[12px] bg-transparent border-none p-0 ${facilityPage === 1 ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >← Prev</button>
                                <span className="text-[11px] text-light-grey">{facilityPage} / {facilityTotalPages} · {facilityPopup.facilities.length} facilities</span>
                                <button
                                    disabled={facilityPage === facilityTotalPages}
                                    onClick={() => setFacilityPage((p) => p + 1)}
                                    className={`text-[12px] bg-transparent border-none p-0 ${facilityPage === facilityTotalPages ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Legacy Facility popup */}
            {legacyFacilityPopup.visible && legacyFacilityPopup.facilities.length > 0 && (() => {
                const totalPages = Math.ceil(legacyFacilityPopup.facilities.length / FACILITY_PAGE_SIZE);
                const pageFacilities = legacyFacilityPopup.facilities.slice((facilityPage - 1) * FACILITY_PAGE_SIZE, facilityPage * FACILITY_PAGE_SIZE);
                return (
                    <div
                        ref={legacyFacilityPopupRef}
                        className="fixed z-[100] w-[280px] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5"
                        style={{ left: legacyFacilityPos.left, top: legacyFacilityPos.top }}
                    >
                        <div className="flex items-center justify-between px-3 pb-2">
                            <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                                {legacyFacilityPopup.facilities.length === 1 ? "1 facility (legacy)" : `${legacyFacilityPopup.facilities.length} facilities (legacy)`}
                            </span>
                            <button
                                onClick={() => setLegacyFacilityPopup({ ...legacyFacilityPopup, visible: false })}
                                className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                            >×</button>
                        </div>
                        {pageFacilities.map((f) => (
                            <div key={f.id} className="px-3 py-2 border-t border-light-light-grey">
                                <div className="text-[13px] font-semibold text-dark-dark-grey mb-0.5">{f.name}</div>
                                {f.address && <div className="text-[11px] text-light-grey mb-1">{f.address}</div>}
                                {isSuperAdmin && f.clientName && (
                                    <div className="text-[11px] text-grey mb-1">{f.clientName}</div>
                                )}
                                <button
                                    onClick={() => {
                                        setFacilityModalData({ id: f.id, name: f.name, address: f.address, clientName: f.clientName, guardianNames: f.guardianNames ?? [] });
                                        setFacilityModalOpen(true);
                                    }}
                                    className="text-[11px] font-semibold text-blue bg-transparent border border-light-blue rounded-md px-2.5 py-1 cursor-pointer inline-block"
                                >
                                    View Details →
                                </button>
                            </div>
                        ))}
                        {totalPages > 1 && (
                            <div className="flex items-center justify-between px-3 pt-2 border-t border-light-light-grey">
                                <button
                                    disabled={facilityPage === 1}
                                    onClick={() => setFacilityPage((p) => p - 1)}
                                    className={`text-[12px] bg-transparent border-none p-0 ${facilityPage === 1 ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >← Prev</button>
                                <span className="text-[11px] text-light-grey">{facilityPage} / {totalPages} · {legacyFacilityPopup.facilities.length} facilities</span>
                                <button
                                    disabled={facilityPage === totalPages}
                                    onClick={() => setFacilityPage((p) => p + 1)}
                                    className={`text-[12px] bg-transparent border-none p-0 ${facilityPage === totalPages ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                                >Next →</button>
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* Facility detail modal */}
            {facilityModalOpen && facilityModalData && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={() => setFacilityModalOpen(false)}>
                    <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.22)] w-[420px] max-w-[95vw] max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-start justify-between mb-4">
                            <h2 className="text-[16px] font-semibold text-dark-dark-grey m-0">{facilityModalData.name}</h2>
                            <button onClick={() => setFacilityModalOpen(false)} className="bg-transparent border-none cursor-pointer text-light-grey text-xl leading-none p-0 ml-3">×</button>
                        </div>
                        {facilityModalData.address && (
                            <div className="mb-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Address</p>
                                <p className="text-[13px] text-dark-dark-grey m-0">{facilityModalData.address}</p>
                            </div>
                        )}
                        {isSuperAdmin && facilityModalData.clientName && (
                            <div className="mb-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Client</p>
                                <p className="text-[13px] text-dark-dark-grey m-0">{facilityModalData.clientName}</p>
                            </div>
                        )}
                        <div className="mb-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Guardians</p>
                            {facilityModalData.guardianNames.length === 0 ? (
                                <p className="text-[13px] text-light-grey m-0">No guardians assigned</p>
                            ) : (
                                facilityModalData.guardianNames.map((name, i) => (
                                    <p key={i} className="text-[13px] text-dark-dark-grey m-0 py-0.5">{name}</p>
                                ))
                            )}
                        </div>
                    </div>
                </div>,
                document.body
            )}

            {/* Zone detail modal */}
            {zoneModalOpen && zoneModalData && ReactDOM.createPortal(
                <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40" onClick={() => setZoneModalOpen(false)}>
                    <div className="bg-white rounded-xl shadow-[0_8px_32px_rgba(0,0,0,0.22)] w-[420px] max-w-[95vw] max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
                        {/* Header */}
                        <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <span
                                    className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ background: zoneModalData.kind === "geofence" ? "#ff7300" : "#00b36b" }}
                                />
                                <h2 className="text-[16px] font-semibold text-dark-dark-grey m-0">{zoneModalData.zone.name}</h2>
                            </div>
                            <button onClick={() => setZoneModalOpen(false)} className="bg-transparent border-none cursor-pointer text-light-grey text-xl leading-none p-0 ml-3">×</button>
                        </div>

                        {/* Type */}
                        <div className="mb-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Type</p>
                            <p className="text-[13px] text-dark-dark-grey m-0">
                                {zoneModalData.kind === "geofence" ? "Geofence" : "Safety Zone"}
                                {" · "}
                                {zoneModalData.zone.location.type === "Point" ? "Circle" : "Polygon"}
                            </p>
                        </div>

                        {/* Client — super admin only */}
                        {isSuperAdmin && zoneModalData.zone.clientName && (
                            <div className="mb-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Client</p>
                                <p className="text-[13px] text-dark-dark-grey m-0">{zoneModalData.zone.clientName}</p>
                            </div>
                        )}

                        {/* Department it belongs to */}
                        {zoneModalData.zone.departmentName && (
                            <div className="mb-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Department</p>
                                <p className="text-[13px] text-dark-dark-grey m-0">{zoneModalData.zone.departmentName}</p>
                            </div>
                        )}

                        {/* Active period */}
                        <div className="mb-3">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Active</p>
                            {zoneModalData.zone.alwaysActive ? (
                                <p className="text-[13px] text-dark-dark-grey m-0">Permanently active</p>
                            ) : (
                                <p className="text-[13px] text-dark-dark-grey m-0">
                                    {zoneModalData.zone.startsAt
                                        ? new Date(zoneModalData.zone.startsAt).toLocaleString()
                                        : "—"}
                                    {" → "}
                                    {zoneModalData.zone.expiresAt
                                        ? new Date(zoneModalData.zone.expiresAt).toLocaleString()
                                        : "—"}
                                </p>
                            )}
                        </div>

                        {/* Applies to — departments */}
                        {zoneModalData.zone.departmentNames.length > 0 && (
                            <div className="mb-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Applies to Departments</p>
                                {zoneModalData.zone.departmentNames.map((d, i) => (
                                    <p key={i} className="text-[13px] text-dark-dark-grey m-0 py-0.5">{d}</p>
                                ))}
                            </div>
                        )}

                        {/* Applies to — users */}
                        {zoneModalData.zone.userNames.length > 0 && (
                            <div className="mb-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Applies to Users</p>
                                {zoneModalData.zone.userNames.map((u, i) => (
                                    <p key={i} className="text-[13px] text-dark-dark-grey m-0 py-0.5">{u}</p>
                                ))}
                            </div>
                        )}

                        {/* Applies to all — when no specific targets */}
                        {zoneModalData.zone.departmentNames.length === 0 && zoneModalData.zone.userNames.length === 0 && (
                            <div className="mb-3">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.06em] text-grey mb-1 mt-0">Applies To</p>
                                <p className="text-[13px] text-light-grey m-0">All users</p>
                            </div>
                        )}
                    </div>
                </div>,
                document.body
            )}

            {/* Overlay feature popup */}
            {overlayPopup.visible && ReactDOM.createPortal(
                <div
                    ref={overlayPopupDivRef}
                    className="fixed z-[150] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] p-3 max-w-[260px]"
                    style={{ left: overlayPos.left, top: overlayPos.top }}
                >
                    <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                            {overlayPopup.name && (
                                <p className="text-[13px] font-semibold text-dark-dark-grey m-0 leading-snug">{overlayPopup.name}</p>
                            )}
                            {overlayPopup.description && (
                                <p className="text-[12px] text-grey m-0 mt-1 leading-snug">{overlayPopup.description}</p>
                            )}
                        </div>
                        <button
                            onClick={() => setOverlayPopup((p) => ({ ...p, visible: false }))}
                            className="bg-transparent border-none cursor-pointer text-light-grey text-lg leading-none p-0 flex-shrink-0 mt-[-2px]"
                        >×</button>
                    </div>
                </div>,
                document.body,
            )}

            {/* Multi-incident popup */}
            {popupVisible && popupIncidents.length > 0 && (
                <div
                    ref={incidentPopupRef}
                    className="fixed z-[100] w-[280px] bg-white rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.18)] py-2.5"
                    style={{ left: incidentPos.left, top: incidentPos.top }}
                >
                    <div className="flex items-center justify-between px-3 pb-2">
                        <span className="text-[12px] font-semibold text-grey uppercase tracking-[0.05em]">
                            {popupTotalCount} incidents
                        </span>
                        <button
                            onClick={() => setPopupVisible(false)}
                            className="bg-transparent border-none cursor-pointer text-light-grey text-base leading-none p-0"
                        >
                            ×
                        </button>
                    </div>

                    {popupIncidents.map((inc) => (
                        <button
                            key={inc.id}
                            onClick={() => onPopupSelectIncident(inc.id)}
                            className="block w-full text-left px-3 py-2 bg-transparent border-none border-t border-light-light-grey cursor-pointer hover:bg-ice"
                        >
                            <div className="text-[12px] font-semibold text-dark-dark-grey mb-0.5 whitespace-nowrap overflow-hidden text-ellipsis">
                                {inc.summary}
                            </div>
                            <div className="text-[11px] text-light-grey">
                                {[inc.displaySeverity ? inc.displaySeverity.charAt(0).toUpperCase() + inc.displaySeverity.slice(1) : undefined, inc.typeName, inc.town].filter(Boolean).join(" · ")}
                            </div>
                        </button>
                    ))}

                    {totalPages > 1 && (
                        <div className="flex items-center justify-between px-3 pt-2 border-t border-light-light-grey">
                            <button
                                disabled={popupPage === 1}
                                onClick={() => onPopupPageChange(popupPage - 1)}
                                className={`text-[12px] bg-transparent border-none p-0 ${popupPage === 1 ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                            >
                                ← Prev
                            </button>
                            <span className="text-[11px] text-light-grey">{popupPage} / {totalPages}</span>
                            <button
                                disabled={popupPage === totalPages}
                                onClick={() => onPopupPageChange(popupPage + 1)}
                                className={`text-[12px] bg-transparent border-none p-0 ${popupPage === totalPages ? "text-light-light-grey cursor-default" : "text-blue cursor-pointer"}`}
                            >
                                Next →
                            </button>
                        </div>
                    )}
                </div>
            )}

            <div
                ref={mapDivRef}
                className="absolute inset-0 w-full h-full"
                style={{ backgroundColor: "#6cd2e7" }}
            />

            <IncidentDetailsModal
                incident={selectedIncident}
                open={modalOpen}
                setOpen={(open) => {
                    setModalOpen(open);
                    if (!open) setPopupVisible(false);
                }}
            />

            <TripDetailsModal
                tripID={tripModalID}
                selectedItemKey={tripModalItemKey}
                open={tripModalOpen}
                setOpen={setTripModalOpen}
                isSuperAdmin={isSuperAdmin}
            />
            <MassWarningModal
                show={showSirenModal}
                setShow={setShowSirenModal}
                clientID={effectiveClientID ?? undefined}
                preloadedUserIDs={sirenUserIDs}
            />
        </div>
    );
}

export default Map;
